import { getCurrentLocale } from './utils';

export type UnitSystem = 'imperial' | 'metric';

// Define unit types
export type UnitType = 
  'length' | 
  'area' | 
  'volume' | 
  'flow' | 
  'pressure' | 
  'temperature' | 
  'power' | 
  'energy' | 
  'electricalEnergy' |
  'speed' | 
  'head';

// Define measurement units with conversion factors to base unit
export interface Unit {
  symbol: string;
  name: string;
  system: UnitSystem;
  conversionToBase: number; // Multiply by this factor to convert to base unit
  displayFormat?: (value: number) => string; // Optional custom display formatter
}

// Define unit groups
export interface UnitGroup {
  baseUnit: string; // The base unit for conversion calculations
  units: Record<string, Unit>;
  defaultImperial: string;
  defaultMetric: string;
}

// Units by type
export const unitGroups: Record<UnitType, UnitGroup> = {
  length: {
    baseUnit: 'm',
    units: {
      'm': { symbol: 'm', name: 'meters', system: 'metric', conversionToBase: 1 },
      'cm': { symbol: 'cm', name: 'centimeters', system: 'metric', conversionToBase: 0.01 },
      'mm': { symbol: 'mm', name: 'millimeters', system: 'metric', conversionToBase: 0.001 },
      'km': { symbol: 'km', name: 'kilometers', system: 'metric', conversionToBase: 1000 },
      'in': { symbol: 'in', name: 'inches', system: 'imperial', conversionToBase: 0.0254 },
      'ft': { symbol: 'ft', name: 'feet', system: 'imperial', conversionToBase: 0.3048 },
      'yd': { symbol: 'yd', name: 'yards', system: 'imperial', conversionToBase: 0.9144 },
      'mi': { symbol: 'mi', name: 'miles', system: 'imperial', conversionToBase: 1609.344 }
    },
    defaultImperial: 'ft',
    defaultMetric: 'm'
  },
  area: {
    baseUnit: 'm²',
    units: {
      'm²': { symbol: 'm²', name: 'square meters', system: 'metric', conversionToBase: 1 },
      'cm²': { symbol: 'cm²', name: 'square centimeters', system: 'metric', conversionToBase: 0.0001 },
      'km²': { symbol: 'km²', name: 'square kilometers', system: 'metric', conversionToBase: 1000000 },
      'in²': { symbol: 'in²', name: 'square inches', system: 'imperial', conversionToBase: 0.00064516 },
      'ft²': { symbol: 'ft²', name: 'square feet', system: 'imperial', conversionToBase: 0.09290304 },
      'yd²': { symbol: 'yd²', name: 'square yards', system: 'imperial', conversionToBase: 0.83612736 },
      'ac': { symbol: 'ac', name: 'acres', system: 'imperial', conversionToBase: 4046.8564224 }
    },
    defaultImperial: 'ft²',
    defaultMetric: 'm²'
  },
  volume: {
    baseUnit: 'm³',
    units: {
      'm³': { symbol: 'm³', name: 'cubic meters', system: 'metric', conversionToBase: 1 },
      'L': { symbol: 'L', name: 'liters', system: 'metric', conversionToBase: 0.001 },
      'mL': { symbol: 'mL', name: 'milliliters', system: 'metric', conversionToBase: 0.000001 },
      'gal': { symbol: 'gal', name: 'gallons', system: 'imperial', conversionToBase: 0.003785411784 },
      'ft³': { symbol: 'ft³', name: 'cubic feet', system: 'imperial', conversionToBase: 0.028316846592 },
      'in³': { symbol: 'in³', name: 'cubic inches', system: 'imperial', conversionToBase: 0.000016387064 }
    },
    defaultImperial: 'gal',
    defaultMetric: 'L'
  },
  flow: {
    baseUnit: 'm³/s',
    units: {
      'm³/s': { symbol: 'm³/s', name: 'cubic meters per second', system: 'metric', conversionToBase: 1 },
      'm³/h': { symbol: 'm³/h', name: 'cubic meters per hour', system: 'metric', conversionToBase: 1/3600 },
      'L/s': { symbol: 'L/s', name: 'liters per second', system: 'metric', conversionToBase: 0.001 },
      'L/min': { symbol: 'L/min', name: 'liters per minute', system: 'metric', conversionToBase: 0.001/60 },
      'GPM': { symbol: 'GPM', name: 'gallons per minute', system: 'imperial', conversionToBase: 0.003785411784/60 },
      'GPH': { symbol: 'GPH', name: 'gallons per hour', system: 'imperial', conversionToBase: 0.003785411784/3600 },
      'CFS': { symbol: 'CFS', name: 'cubic feet per second', system: 'imperial', conversionToBase: 0.028316846592 }
    },
    defaultImperial: 'GPM',
    defaultMetric: 'L/min'
  },
  pressure: {
    baseUnit: 'Pa',
    units: {
      'Pa': { symbol: 'Pa', name: 'pascals', system: 'metric', conversionToBase: 1 },
      'kPa': { symbol: 'kPa', name: 'kilopascals', system: 'metric', conversionToBase: 1000 },
      'MPa': { symbol: 'MPa', name: 'megapascals', system: 'metric', conversionToBase: 1000000 },
      'bar': { symbol: 'bar', name: 'bar', system: 'metric', conversionToBase: 100000 },
      'psi': { symbol: 'psi', name: 'pounds per square inch', system: 'imperial', conversionToBase: 6894.75729 },
      'inHg': { symbol: 'inHg', name: 'inches of mercury', system: 'imperial', conversionToBase: 3386.389 }
    },
    defaultImperial: 'psi',
    defaultMetric: 'bar'
  },
  temperature: {
    baseUnit: 'K',
    units: {
      'K': { 
        symbol: 'K', 
        name: 'kelvin', 
        system: 'metric', 
        conversionToBase: 1,
        displayFormat: (value: number) => value.toFixed(1)
      },
      '°C': { 
        symbol: '°C', 
        name: 'celsius', 
        system: 'metric', 
        conversionToBase: 1,
        // Special conversion for temperatures
        displayFormat: (value: number) => {
          const celsius = value;
          return celsius.toFixed(1);
        }
      },
      '°F': { 
        symbol: '°F', 
        name: 'fahrenheit', 
        system: 'imperial', 
        conversionToBase: 5/9,
        // Special conversion for temperatures
        displayFormat: (value: number) => {
          const fahrenheit = value;
          return fahrenheit.toFixed(1);
        }
      }
    },
    defaultImperial: '°F',
    defaultMetric: '°C'
  },
  power: {
    baseUnit: 'W',
    units: {
      'W': { symbol: 'W', name: 'watts', system: 'metric', conversionToBase: 1 },
      'kW': { symbol: 'kW', name: 'kilowatts', system: 'metric', conversionToBase: 1000 },
      'MW': { symbol: 'MW', name: 'megawatts', system: 'metric', conversionToBase: 1000000 },
      'hp': { symbol: 'hp', name: 'horsepower', system: 'imperial', conversionToBase: 745.7 }
    },
    defaultImperial: 'hp',
    defaultMetric: 'kW'
  },
  electricalEnergy: {
    baseUnit: 'kWh',
    units: {
      'Wh': { symbol: 'Wh', name: 'watt-hours', system: 'metric', conversionToBase: 0.001 },
      'kWh': { symbol: 'kWh', name: 'kilowatt-hours', system: 'metric', conversionToBase: 1 },
      'MWh': { symbol: 'MWh', name: 'megawatt-hours', system: 'metric', conversionToBase: 1000 },
      'GWh': { symbol: 'GWh', name: 'gigawatt-hours', system: 'metric', conversionToBase: 1000000 }
    },
    defaultImperial: 'kWh', // Electrical units are universal
    defaultMetric: 'kWh'
  },
  energy: {
    baseUnit: 'J',
    units: {
      'J': { symbol: 'J', name: 'joules', system: 'metric', conversionToBase: 1 },
      'kJ': { symbol: 'kJ', name: 'kilojoules', system: 'metric', conversionToBase: 1000 },
      'MJ': { symbol: 'MJ', name: 'megajoules', system: 'metric', conversionToBase: 1000000 },
      'GJ': { symbol: 'GJ', name: 'gigajoules', system: 'metric', conversionToBase: 1000000000 },
      'BTU': { symbol: 'BTU', name: 'british thermal units', system: 'imperial', conversionToBase: 1055.06 },
      'therm': { symbol: 'therm', name: 'therms', system: 'imperial', conversionToBase: 105506000 }
    },
    defaultImperial: 'BTU',
    defaultMetric: 'MJ'
  },
  speed: {
    baseUnit: 'm/s',
    units: {
      'm/s': { symbol: 'm/s', name: 'meters per second', system: 'metric', conversionToBase: 1 },
      'km/h': { symbol: 'km/h', name: 'kilometers per hour', system: 'metric', conversionToBase: 1/3.6 },
      'ft/s': { symbol: 'ft/s', name: 'feet per second', system: 'imperial', conversionToBase: 0.3048 },
      'mph': { symbol: 'mph', name: 'miles per hour', system: 'imperial', conversionToBase: 0.44704 }
    },
    defaultImperial: 'mph',
    defaultMetric: 'km/h'
  },
  head: {
    baseUnit: 'm',
    units: {
      'm': { symbol: 'm', name: 'meters', system: 'metric', conversionToBase: 1 },
      'ft': { symbol: 'ft', name: 'feet', system: 'imperial', conversionToBase: 0.3048 }
    },
    defaultImperial: 'ft',
    defaultMetric: 'm'
  }
};

/**
 * Get the user's preferred unit system based on locale
 * @returns The preferred unit system ('metric' or 'imperial')
 */
export function getPreferredUnitSystem(): UnitSystem {
  const locale = getCurrentLocale();
  
  // Countries that primarily use imperial units
  const imperialLocales = ['en-US', 'en-LR', 'en-MM'];
  
  return imperialLocales.includes(locale) ? 'imperial' : 'metric';
}

/**
 * Get the preferred unit for a specific measurement type
 * @param type The type of measurement
 * @param system Optional override for unit system
 * @returns The preferred unit symbol
 */
export function getPreferredUnit(type: UnitType, system?: UnitSystem): string {
  const unitSystem = system || getPreferredUnitSystem();
  const group = unitGroups[type];
  
  return unitSystem === 'imperial' ? group.defaultImperial : group.defaultMetric;
}

/**
 * Convert a value from one unit to another
 * @param value The value to convert
 * @param fromUnit The source unit symbol
 * @param toUnit The target unit symbol
 * @param type The measurement type
 * @returns The converted value
 */
export function convertUnit(
  value: number,
  fromUnit: string,
  toUnit: string,
  type: UnitType
): number {
  const group = unitGroups[type];
  
  // Special case for temperature
  if (type === 'temperature') {
    // Convert to Kelvin first (our base unit for temperature)
    let kelvin: number;
    
    if (fromUnit === '°C') {
      kelvin = value + 273.15;
    } else if (fromUnit === '°F') {
      kelvin = (value - 32) * 5/9 + 273.15;
    } else {
      kelvin = value; // Already Kelvin
    }
    
    // Convert from Kelvin to target unit
    if (toUnit === '°C') {
      return kelvin - 273.15;
    } else if (toUnit === '°F') {
      return (kelvin - 273.15) * 9/5 + 32;
    } else {
      return kelvin; // Return as Kelvin
    }
  }
  
  // For all other unit types, use conversion factors
  const fromUnitDef = group.units[fromUnit];
  const toUnitDef = group.units[toUnit];
  
  if (!fromUnitDef || !toUnitDef) {
    console.error(`Invalid unit conversion from ${fromUnit} to ${toUnit} for type ${type}`);
    return value;
  }
  
  // Convert to base unit first, then to target unit
  const valueInBaseUnit = value * fromUnitDef.conversionToBase;
  return valueInBaseUnit / toUnitDef.conversionToBase;
}

/**
 * Format a value with its unit symbol, converting to preferred units if needed
 * @param value The value to format
 * @param unit The source unit symbol
 * @param type The measurement type
 * @param options Optional formatting options
 * @returns Formatted string with appropriate unit
 */
export function formatWithUnit(
  value: number,
  unit: string,
  type: UnitType,
  options: {
    decimals?: number;
    system?: UnitSystem;
    forceUnit?: string;
  } = {}
): string {
  const { decimals = 2, system, forceUnit } = options;
  
  // Determine target unit
  const targetUnit = forceUnit || getPreferredUnit(type, system);
  
  // Convert the value if needed
  const convertedValue = unit !== targetUnit
    ? convertUnit(value, unit, targetUnit, type)
    : value;
  
  // Format the value
  const group = unitGroups[type];
  const unitDef = group.units[targetUnit];
  
  if (!unitDef) {
    console.error(`Unknown unit ${targetUnit} for type ${type}`);
    return `${value.toFixed(decimals)} ${unit}`;
  }
  
  // Use custom formatter if available
  if (unitDef.displayFormat) {
    return `${unitDef.displayFormat(convertedValue)} ${unitDef.symbol}`;
  }
  
  // Default formatting
  return `${convertedValue.toFixed(decimals)} ${unitDef.symbol}`;
}

/**
 * Get the full unit information for a given symbol
 * @param symbol The unit symbol (e.g., 'kW', 'm')
 * @returns The unit information or undefined if not found
 */
export function getUnitBySymbol(symbol: string): Unit | undefined {
  for (const type in unitGroups) {
    const group = unitGroups[type as UnitType];
    if (group.units[symbol]) {
      return group.units[symbol];
    }
  }
  return undefined;
}

/**
 * Get the measurement type for a given unit symbol
 * @param symbol The unit symbol (e.g., 'kW', 'm')
 * @returns The measurement type or undefined if not found
 */
export function getTypeForUnit(symbol: string): UnitType | undefined {
  for (const type in unitGroups) {
    const group = unitGroups[type as UnitType];
    if (group.units[symbol]) {
      return type as UnitType;
    }
  }
  return undefined;
}